import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Banknote, Users } from 'lucide-react';
import api from '../utils/axios';
import Dropdown from '../components/Dropdown';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { canCreateSalarySheet, canExportMeezan, canExportEmployees as mayExportEmployees } from '../utils/reportPermissions';
import CModal from '../components/shared/c-modal';
import { EMPLOYMENT_TYPES } from '../constants';

const NewReportTypeSelect = () => {
  const navigate = useNavigate();

  const { hasPermission } = useAuth();
  const canExportEmployees = mayExportEmployees(hasPermission);
  const canReadReports = hasPermission('reports', 'read');

  // The report types the server lets this caller reach (a pay-bearing one needs
  // salaries:read_all). The pay cards wait for it, so they cannot pop in and shift the grid.
  // null while loading; [] when the caller cannot list types at all or the call fails.
  const [reachableTypes, setReachableTypes] = useState(canReadReports ? null : []);
  useEffect(() => {
    if (!canReadReports) return undefined;
    let cancelled = false;
    api.get('/api/reports/types')
      .then((res) => { if (!cancelled) setReachableTypes((res.data.data || []).map((t) => t.id)); })
      .catch(() => { if (!cancelled) setReachableTypes([]); });
    return () => { cancelled = true; };
  }, [canReadReports]);
  const salarySheetReachable = Boolean(reachableTypes?.includes('salary_sheet'));
  const [showEmployeeExportModal, setShowEmployeeExportModal] = useState(false);
  const [employeeExportLoading, setEmployeeExportLoading] = useState(false);
  const [empExportDepartments, setEmpExportDepartments] = useState([]);
  const [empExportCatalog, setEmpExportCatalog] = useState([]);
  const [empExportFilters, setEmpExportFilters] = useState({
    department: '',
    status: '',
    employment_type: '',
    hire_date_from: '',
    hire_date_to: '',
  });
  const [empExportSelectedColumns, setEmpExportSelectedColumns] = useState(new Set());
  const [showExportError, setShowExportError] = useState(false);
  const [exportErrorMsg, setExportErrorMsg] = useState('');
  // Monotonic token identifying the current open cycle. Each open mints a new id;
  // only the fetch whose id still matches commits its result. Closing (or reopening)
  // bumps the id, so an in-flight request from a prior cycle no-ops on resolve,
  // preventing stale data overwrites and spurious error modals on close→reopen.
  const activeRequestIdRef = useRef(0);

  const openEmployeeExportModal = async () => {
    const requestId = ++activeRequestIdRef.current;
    setShowEmployeeExportModal(true);
    try {
      const [deptRes, colRes] = await Promise.all([
        api.get('/api/departments'),
        api.get('/api/employees/export/columns'),
      ]);
      if (requestId !== activeRequestIdRef.current) return;
      const departments = Array.isArray(deptRes.data?.data) ? deptRes.data.data : (deptRes.data || []);
      setEmpExportDepartments(departments);
      const catalog = Array.isArray(colRes.data?.data) ? colRes.data.data : [];
      setEmpExportCatalog(catalog);
      // Default selection: all non-salary columns checked
      setEmpExportSelectedColumns(new Set(catalog.filter(c => !c.salaryGated).map(c => c.key)));
    } catch (err) {
      if (requestId !== activeRequestIdRef.current) return;
      setExportErrorMsg('Failed to load export options');
      setShowExportError(true);
      setShowEmployeeExportModal(false);
    }
  };

  const closeEmployeeExportModal = () => {
    activeRequestIdRef.current++;
    setShowEmployeeExportModal(false);
    setEmpExportCatalog([]);
    setEmpExportSelectedColumns(new Set());
    setEmpExportFilters({ department: '', status: '', employment_type: '', hire_date_from: '', hire_date_to: '' });
  };

  const toggleEmpExportColumn = (key) => {
    setEmpExportSelectedColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleEmployeeExport = async () => {
    if (empExportSelectedColumns.size === 0) return;
    setEmployeeExportLoading(true);
    try {
      // Use the shared axios instance (utils/axios.js): it resolves the backend
      // origin from REACT_APP_API_BASE_URL (defaulting to localhost:9000) and
      // attaches the auth token via its request interceptor, so the export hits
      // the same backend as every other call, including the static `serve`
      // host used in e2e, where the frontend (:3000) and backend (:9000) differ.
      const params = { columns: Array.from(empExportSelectedColumns).join(',') };
      if (empExportFilters.department) params.department = empExportFilters.department;
      if (empExportFilters.status) params.status = empExportFilters.status;
      if (empExportFilters.employment_type) params.employment_type = empExportFilters.employment_type;
      if (empExportFilters.hire_date_from) params.hire_date_from = empExportFilters.hire_date_from;
      if (empExportFilters.hire_date_to) params.hire_date_to = empExportFilters.hire_date_to;

      const res = await api.get('/api/employees/export', { params, responseType: 'blob' });
      const downloadUrl = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'employees-export.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
      closeEmployeeExportModal();
    } catch (err) {
      setExportErrorMsg('Export failed');
      setShowExportError(true);
    } finally {
      setEmployeeExportLoading(false);
    }
  };

  const reportTypes = [
    // Both pay cards need a salary sheet the server lets this caller reach, and the verb.
    ...(salarySheetReachable && canCreateSalarySheet(hasPermission) ? [{
      type: 'salary_sheet',
      label: 'Salary Sheet',
      icon: <FileText size={48} className="text-primary-600 mb-6" />,
      description: 'Generate a monthly salary sheet for all employees, with PKR/USD breakdown.',
      actionLabel: 'Create',
      actionTestId: 'reports-type-continue-salary_sheet',
      onAction: () => navigate('/reports/new/salary_sheet'),
    }] : []),
    ...(salarySheetReachable && canExportMeezan(hasPermission) ? [{
      type: 'meezan_bank_transfer_sheet',
      label: 'Meezan Bank Transfer Sheet',
      icon: <Banknote size={48} className="text-green-600 mb-6" />,
      description: 'Export a bank transfer sheet for Meezan Bank employees, based on the generated Salary Sheet. This report is available for export only.',
      actionLabel: 'Create',
      actionTestId: 'reports-type-continue-meezan_bank_transfer_sheet',
      onAction: () => navigate('/reports/new/meezan_bank_transfer_sheet'),
    }] : []),
    // Employee roster export, gated by employees:read_*; opens the export modal
    // instead of navigating to a generator form.
    ...(canExportEmployees ? [{
      type: 'employee_export',
      label: 'Employee Export',
      icon: <Users size={48} className="text-blue-600 mb-6" />,
      description: 'Export the employee roster to Excel. Filter by department, status, employment type and hire date, and pick which columns to include.',
      actionLabel: 'Export',
      actionTestId: 'reports-export',
      onAction: openEmployeeExportModal,
    }] : []),
    // Add more report types here as needed
  ];

  return (
    <div className="max-w-4xl mx-auto py-12">
      <h2 className="text-3xl font-bold mb-10 text-center">Select Report Type</h2>
      {reachableTypes === null && <LoadingSpinner size="lg" />}
      {reachableTypes !== null && reportTypes.length === 0 && (
        <p data-testid="reports-type-none" className="text-center text-gray-500">
          There are no reports you can create.
        </p>
      )}
      {reachableTypes !== null && reportTypes.length > 0 && (
      <div className="flex justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 w-full" data-testid="reports-type-select">
          {reportTypes.map((type) => (
            <div
              key={type.type}
              data-testid={`reports-type-option-${type.type}`}
              className="border rounded-xl p-8 flex flex-col items-center bg-white shadow-soft hover:shadow-lg transition min-h-[320px] h-full"
            >
              {type.icon}
              <div className="font-semibold text-xl mb-2 text-center">{type.label}</div>
              <div className="my-4" />
              <div className="text-gray-500 text-base mb-6 text-center flex-1">{type.description}</div>
              <button
                data-testid={type.actionTestId}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded font-semibold shadow hover:bg-primary-700 transition"
                onClick={type.onAction}
              >
                {type.actionLabel}
              </button>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Employee Bulk Export Modal */}
      {showEmployeeExportModal && (
        <CModal
          onClose={closeEmployeeExportModal}
          labelledBy="employee-export-title"
          dismissible={!employeeExportLoading}
          // Closing resets the typed filters and the column choice.
          closeOnBackdrop={false}
          className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-6 relative max-h-[90vh] overflow-y-auto"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={closeEmployeeExportModal}
            disabled={employeeExportLoading}
          >
            &times;
          </button>
          <h2 id="employee-export-title" className="text-xl font-bold mb-1 text-gray-900">Export Employees</h2>
          <p className="text-sm text-gray-600 mb-4">Filter the roster, pick the columns you need, and download an Excel file.</p>

          {empExportCatalog.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">Loading export options...</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <Dropdown
                    value={empExportFilters.department}
                    onChange={(e) => setEmpExportFilters(p => ({ ...p, department: e.target.value }))}
                  >
                    <option value="">All</option>
                    {empExportDepartments.map(d => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </Dropdown>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <Dropdown
                    value={empExportFilters.status}
                    onChange={(e) => setEmpExportFilters(p => ({ ...p, status: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="terminated">Terminated</option>
                  </Dropdown>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                  <Dropdown
                    value={empExportFilters.employment_type}
                    onChange={(e) => setEmpExportFilters(p => ({ ...p, employment_type: e.target.value }))}
                  >
                    <option value="">All</option>
                    {EMPLOYMENT_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                  </Dropdown>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date From</label>
                  <input
                    type="date"
                    value={empExportFilters.hire_date_from}
                    onChange={(e) => setEmpExportFilters(p => ({ ...p, hire_date_from: e.target.value }))}
                    max={empExportFilters.hire_date_to || undefined}
                    data-testid="reports-field-date-from"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date To</label>
                  <input
                    type="date"
                    value={empExportFilters.hire_date_to}
                    onChange={(e) => setEmpExportFilters(p => ({ ...p, hire_date_to: e.target.value }))}
                    min={empExportFilters.hire_date_from || undefined}
                    data-testid="reports-field-date-to"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Columns to include</label>
                  <div className="flex gap-2 text-xs">
                    <button
                      type="button"
                      className="text-primary-600 hover:text-primary-800"
                      onClick={() => setEmpExportSelectedColumns(new Set(empExportCatalog.map(c => c.key)))}
                    >
                      Select all
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      className="text-primary-600 hover:text-primary-800"
                      onClick={() => setEmpExportSelectedColumns(new Set())}
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 border border-gray-200 rounded-lg p-3 bg-gray-50">
                  {empExportCatalog.map(col => (
                    <label key={col.key} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={empExportSelectedColumns.has(col.key)}
                        onChange={() => toggleEmpExportColumn(col.key)}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                      />
                      <span>
                        {col.header}
                        {col.salaryGated && <span className="ml-1 text-xs text-amber-600">(salary)</span>}
                      </span>
                    </label>
                  ))}
                </div>
                {empExportSelectedColumns.size === 0 && (
                  <p className="mt-1 text-xs text-danger-600">Select at least one column.</p>
                )}
              </div>
            </>
          )}

          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              onClick={closeEmployeeExportModal}
              disabled={employeeExportLoading}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleEmployeeExport}
              disabled={employeeExportLoading || empExportSelectedColumns.size === 0}
            >
              {employeeExportLoading ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </CModal>
      )}

      {/* Export Error Modal */}
      {showExportError && (
        <CModal
          onClose={() => setShowExportError(false)}
          labelledBy="export-error-title"
          className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative"
        >
          <button
            aria-label="Close"
            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 text-2xl font-bold"
            onClick={() => setShowExportError(false)}
          >
            &times;
          </button>
          <h2 id="export-error-title" className="text-xl font-bold mb-4 text-danger-700">Error</h2>
          <p className="mb-4 text-gray-700">{exportErrorMsg}</p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700 font-semibold"
              onClick={() => setShowExportError(false)}
            >
              Close
            </button>
          </div>
        </CModal>
      )}
    </div>
  );
};

export default NewReportTypeSelect;
